import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import DevLogin from "@/components/DevLogin";
import AppLayout from "@/layouts/AppLayout";
import CanvasPage from "@/pages/CanvasPage";
import SouschefStudio from "@/pages/SouschefStudio";
import LibraryPage from "@/pages/LibraryPage";
import DraftsPage from "@/pages/DraftsPage";
import { getToken, clearToken, verify, getConfig } from "@/lib/atlasApi";
import { beginSsoLogin, captureAndCompleteAuth } from "@/lib/sso";
import { Toaster } from "@/components/ui/sonner";
import logo from "@/assets/logo.png";

function SsoScreen({ config, error }) {
  const enabled = Boolean(config?.sso_login_url && config?.sso_client_id);
  const startLogin = () => {
    if (!enabled) return;
    // Reinício limpo: descarta token/cookies/PKCE corrompidos antes do novo fluxo.
    clearToken();
    beginSsoLogin(config);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[#2A364F] bg-[#111827] p-8 text-center">
        <img src={logo} alt="FAWKES ATLAS" className="h-16 w-16 object-contain mx-auto" />
        <h1 className="text-xl font-bold text-white mt-4">FAWKES ATLAS CORE</h1>
        <p className="text-sm text-slate-400 mt-1">Enciclopédia Gastronômica · ecossistema SOP &amp; RAFT</p>
        {error && (
          <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300" data-testid="sso-error">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={startLogin}
          disabled={!enabled}
          className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E05A2B] py-3 text-white font-medium ${enabled ? "hover:bg-[#F06B3C]" : "opacity-60 cursor-not-allowed"}`}
          data-testid="sso-login-button"
        >
          Entrar com o SOP &amp; RAFT
        </button>
        {!enabled && <p className="text-[11px] text-slate-500 mt-3">SSO oficial em configuração (client_id pendente). Acesso via conta provisionada do SOP &amp; RAFT.</p>}
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [checking, setChecking] = useState(true);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    (async () => {
      let cfg;
      try { cfg = await getConfig(); } catch { cfg = { dev_login_enabled: true }; }
      setConfig(cfg);

      // Listener imediato de retorno do SSO: captura code/token/access_token
      // (query OU hash), armazena e conclui a autenticação.
      const result = await captureAndCompleteAuth(cfg);
      if (result.status === "error") {
        setAuthError(
          `Não foi possível concluir o login com o SOP & RAFT.${result.detail ? ` (${result.detail})` : ""}`
        );
      }

      if (getToken()) {
        try {
          setUser(await verify());
        } catch {
          // Token rejeitado: limpa COMPLETAMENTE a sessão (localStorage/cookies/PKCE).
          clearToken();
          // Só alerta se o usuário ACABOU de voltar do SSO com credencial inválida.
          // Em reload comum com token velho, limpa silenciosamente e mostra o login.
          if (result.status === "ok") {
            setAuthError("Sessão inválida ou expirada. Faça login novamente.");
          }
        }
      }
      setChecking(false);
    })();
  }, []);

  const logout = () => { clearToken(); setUser(null); setAuthError(""); };

  // Produção usa ESTRITAMENTE o SSO oficial do SOP & RAFT. O painel de
  // desenvolvimento só existe em preview/localhost — nunca no domínio de produção,
  // independentemente da flag do backend (defesa em profundidade).
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPreviewOrLocal =
    /localhost|127\.0\.0\.1/.test(host) || host.endsWith(".preview.emergentagent.com");
  const forceSso =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("screen") === "sso";
  const devLoginEnabled = !forceSso && isPreviewOrLocal && config?.dev_login_enabled !== false;

  return (
    <div className="dark atlas-root min-h-screen bg-[#0B0F17] text-[#F8FAFC]" style={{ fontFamily: "Inter, sans-serif" }}>
      <Toaster />
      {checking ? (
        <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando…</div>
      ) : !user ? (
        devLoginEnabled ? <DevLogin onLogin={setUser} /> : <SsoScreen config={config} error={authError} />
      ) : (
        <BrowserRouter>
          <Routes>
            <Route element={<AppLayout user={user} onLogout={logout} />}>
              <Route index element={<Navigate to="/teia" replace />} />
              <Route path="/teia" element={<CanvasPage />} />
              <Route path="/canvas" element={<Navigate to="/teia" replace />} />
              <Route path="/souschef" element={<SouschefStudio />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/drafts" element={<DraftsPage />} />
              <Route path="*" element={<Navigate to="/teia" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      )}
    </div>
  );
}

export default App;
